import { EventBus } from "../EventBus.js";
import type { Position } from "../types/grid.js";

// ── Noise radii per action type (in cells) ───────────────────────────────────

export const NOISE_RADIUS = {
  movement: 2,
  weaponSwing: 4,
  spellCast: 2,
  doorBash: 6,
  silent: 0,
} as const;

export type NoiseSource = keyof typeof NOISE_RADIUS;

// ── Registry entry ───────────────────────────────────────────────────────────

interface EnemyEntry {
  position: Position;
  hearingRadius: number;
  onAlerted: (noiseCell: Position) => void;
}

// ── NoiseBroadcast ───────────────────────────────────────────────────────────

/**
 * Singleton noise-broadcast service.
 *
 * Callers emit noise via `noiseBroadcast.emitNoise(cell, radius)` (or by
 * emitting `perception:noise` on the EventBus directly). Enemies register an
 * `onAlerted` callback; the service dispatches to any enemy whose hearing
 * radius reaches the noise cell.
 *
 * An enemy hears a noise when:
 *   Manhattan(enemy.position, noiseCell) ≤ noiseRadius
 *   AND
 *   Manhattan(enemy.position, noiseCell) ≤ enemy.hearingRadius
 */
class NoiseBroadcastImpl {
  private readonly enemies = new Map<string, EnemyEntry>();

  constructor() {
    EventBus.on("perception:noise", ({ cell, radius }) => {
      for (const entry of this.enemies.values()) {
        const dist =
          Math.abs(entry.position.x - cell.x) + Math.abs(entry.position.z - cell.z);
        if (dist <= radius && dist <= entry.hearingRadius) {
          entry.onAlerted(cell);
        }
      }
    });
  }

  registerEnemy(
    instanceId: string,
    position: Position,
    hearingRadius: number,
    onAlerted: (noiseCell: Position) => void,
  ): void {
    this.enemies.set(instanceId, { position, hearingRadius, onAlerted });
  }

  unregisterEnemy(instanceId: string): void {
    this.enemies.delete(instanceId);
  }

  updateEnemyPosition(instanceId: string, position: Position): void {
    const entry = this.enemies.get(instanceId);
    if (entry) entry.position = position;
  }

  emitNoise(cell: Position, radius: number): void {
    EventBus.emit("perception:noise", { cell, radius });
  }

  /** Remove all registered enemies. Intended for test teardown. */
  clear(): void {
    this.enemies.clear();
  }
}

export const noiseBroadcast = new NoiseBroadcastImpl();
